import React from "react";
import {Box, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {CallOutgoing01Icon} from "@hugeicons/core-free-icons";

// Pieces of a conversation row, shared by the conversations table and the conversation modal

export const STATUS_STYLES = {
  ACTIVE: {bg: "#EEF0FE", color: "#4B55C9"},
  FINISHED: {bg: "#E8F7EF", color: "#1E8A55"},
  SUCCESS: {bg: "#E8F7EF", color: "#1E8A55"},
  REDIRECTED: {bg: "#F1EEFB", color: "#6E5BC4"},
  HANDOVER: {bg: "#F1EEFB", color: "#6E5BC4"},
  AGENT_CALL_INITIATED: {bg: "#F1EEFB", color: "#6E5BC4"},
  HANGUP: {bg: "#FDF4E3", color: "#A86A0C"},
  USER_NOT_TALKING: {bg: "#FDF4E3", color: "#A86A0C"},
  MISSED_CALL: {bg: "#FDECEC", color: "#C73A3A"},
  FAILED: {bg: "#FDECEC", color: "#C73A3A"},
};

export const AVATAR_COLORS = [
  {bg: "#EEF0FE", color: "#4B55C9"},
  {bg: "#E7F1F8", color: "#3D7AA6"},
  {bg: "#F1EEFB", color: "#6E5BC4"},
  {bg: "#E8F7EF", color: "#1E8A55"},
  {bg: "#FDF4E3", color: "#A86A0C"},
];

export const formatStatus = (status) => status
  ? status.toLowerCase().replaceAll("_", " ").replace(/^\w/, c => c.toUpperCase())
  : "—";


// Initials of a name; phone numbers / ids get no initials (an icon is shown instead)
export const initialsOf = (name) => {
  if (!name || !/[a-zA-Z]/.test(name)) return null;
  return name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase();
};

export const StatusBadge = ({conversation}) => {
  const style = STATUS_STYLES[conversation?.status] || {bg: "#F2F2F7", color: "#6B6B80"};
  const target = conversation?.vdn || conversation?.transferNumber;
  return (
    <Tooltip title={target ? `To ${target}` : ""}>
      <Box component={"span"} sx={{
        display: "inline-flex", alignItems: "center", gap: 0.75, px: 1, py: 0.25, borderRadius: "999px",
        fontSize: 12, fontWeight: 500, whiteSpace: "nowrap", bgcolor: style.bg, color: style.color,
      }}>
        <Box component={"span"} sx={{width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor"}}/>
        {formatStatus(conversation?.status)}
      </Box>
    </Tooltip>
  );
};

// Number the conversation was transferred to (redirected / handed over), shown after the caller; nothing when
// it was not transferred
export const TransferNumber = ({conversation, size = "small"}) => {
  const target = conversation?.transferNumber || conversation?.vdn;
  if (!target) return null;
  return (
    <Tooltip title={`Transferred to ${target}`}>
      <Box component={"span"} sx={{
        display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0, px: 0.75, py: 0.125,
        borderRadius: "6px", fontSize: size === "small" ? 11 : 12, fontWeight: 600, whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums", color: "#6E5BC4", bgcolor: "#F1EEFB",
      }}>
        <HugeiconsIcon icon={CallOutgoing01Icon} size={size === "small" ? 12 : 13}/>
        {target}
      </Box>
    </Tooltip>
  );
};

export const TagPills = ({names}) => {
  if (!names.length) return <Typography sx={{fontSize: 12, color: "#B4B4C3"}}>—</Typography>;
  const [first, ...rest] = names;
  return (
    <Tooltip title={names.join(", ")}>
      <Stack direction={"row"} gap={0.5} alignItems={"center"} sx={{minWidth: 0}}>
        <Box component={"span"} sx={{
          px: 1, py: 0.25, borderRadius: "6px", fontSize: 12, color: "#4A4A63", bgcolor: "#F2F2F7",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0,
        }}>
          {first}
        </Box>
        {rest.length > 0 && <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>+{rest.length}</Typography>}
      </Stack>
    </Tooltip>
  );
};
